// ====================== BLOCK 1/3 (상단 ~ 상태/복원 로직 끝) ======================
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { authFetch } from "../lib/auth";
import { calcPaymentAmounts } from "../lib/paymentAmounts";
import type { User } from "../context/AuthProvider";
import { isVerificationExempt } from "../lib/verification";
import { useVerificationStatus } from "../hooks/useVerificationStatus";
import VerificationStatusNotice from "../components/design/VerificationStatusNotice";
import PaymentStepFrame from "../components/design/PaymentStepFrame";
import { parseRegistrationAmount } from "../lib/registrationDraft";
import {
  allowedTransferStep, hasTransferDocumentHistory, nextTransferView, readTransferView,
  transferBackDelta, transferDocumentPath, transferStepTitle, transferViewState,
  type TransferStep, type TransferView,
} from "../lib/transferFlow";

const API_BASE = (import.meta.env.VITE_API_BASE || "").replace(/\/$/, "");
const VAT_NOTICE_VERSION = "2026-05-08.v1";

// ✅ 이체금액 상한: 2,000만원 이하(원금 기준)
const MAX_AMOUNT = 20_000_000;

// 이체 수수료 3.7%, 부가세 10% (PG 결제 건(트랜잭션)마다 계산되는 전제)
function calcFeeVatTotal(amount: number) {
  return calcPaymentAmounts(amount);
}

/* 이름 정규화(불일치 오탐 방지) */
function normalizeHolderName(v: unknown) {
  return String(v ?? "")
    .normalize("NFKC")
    .replace(/\s+/g, "")
    .replace(/[·ㆍ•.]/g, "")
    .trim();
}

/** 에이픽 계좌실명조회용 은행/증권 코드 목록 */
const BANKS = [
  // 은행
  { code: "004", name: "국민" },
  { code: "088", name: "신한" },
  { code: "003", name: "기업" },
  { code: "020", name: "우리" },
  { code: "011", name: "농협" },
  { code: "002", name: "산업" },
  { code: "007", name: "수협" },
  { code: "048", name: "신협" },
  { code: "081", name: "하나" },
  { code: "027", name: "한국씨티" },
  { code: "090", name: "카카오뱅크" },
  { code: "089", name: "케이뱅크" },
  { code: "092", name: "토스뱅크" },
  { code: "039", name: "경남" },
  { code: "034", name: "광주" },
  { code: "031", name: "대구" },
  { code: "032", name: "부산" },
  { code: "037", name: "전북" },
  { code: "045", name: "새마을" },
  { code: "071", name: "우체국" },
  { code: "050", name: "저축은행" },
  { code: "012", name: "지역농.축협" },
  { code: "055", name: "도이치" },
  { code: "063", name: "중국" },
  { code: "067", name: "중국건설" },
  { code: "062", name: "중국공상" },
  { code: "061", name: "BNP파리바" },
  { code: "060", name: "BOA" },
  { code: "054", name: "HSBC" },
  { code: "057", name: "JP모간" },
  { code: "023", name: "SC" },
  { code: "064", name: "산림조합" },
  // 증권사
  { code: "278", name: "신한투자증권" },
  { code: "261", name: "교보증권" },
  { code: "227", name: "다올투자증권" },
  { code: "267", name: "대신증권" },
  { code: "287", name: "메리츠증권" },
  { code: "238", name: "미래에셋증권" },
  { code: "290", name: "부국증권" },
  { code: "240", name: "삼성증권" },
  { code: "221", name: "상상인증권" },
  { code: "291", name: "신영증권" },
  { code: "266", name: "에스케이증권" },
  { code: "209", name: "유안타증권" },
  { code: "280", name: "유진투자증권" },
  { code: "265", name: "이베스트투자증권" },
  { code: "288", name: "카카오페이증권" },
  { code: "292", name: "케이프투자증권" },
  { code: "264", name: "키움증권" },
  { code: "271", name: "토스증권" },
  { code: "294", name: "한국포스증권" },
  { code: "270", name: "하나증권" },
  { code: "262", name: "하이투자증권" },
  { code: "243", name: "한국투자증권" },
  { code: "269", name: "한화투자증권" },
  { code: "263", name: "현대차증권" },
  { code: "224", name: "BNK투자증권" },
  { code: "279", name: "DB금융투자" },
  { code: "225", name: "IBK투자증권" },
  { code: "218", name: "KB증권" },
  { code: "247", name: "NH투자증권" },
];

type VerifyResp = {
  ok: boolean;
  matched?: boolean;
  reason?: string;
  holderNameInput?: string;
  vendorHolderName?: string | null;
  vendorInfo?: any;
  error?: string;
};

type SplitCount = 1 | 2 | 3;

function onlyDigitsToNumber(v: string) {
  const raw = String(v || "").replace(/[^\d]/g, "");
  if (!raw) return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

export default function ContractTransfer() {
  const { account, loading, error, retry } = useVerificationStatus();
  if (loading || error || !account) return <VerificationStatusNotice error={error} retry={retry} />;
  return <ContractTransferForm key={account.id} user={account} />;
}

function ContractTransferForm({ user }: { user: User }) {
  const nav = useNavigate();
  const location = useLocation();
  const [sp] = useSearchParams();
  const amountEntry = sp.get("entry") === "amount";
  const skipAccountVerify = isVerificationExempt(user);

  // 카테고리 + 업로드 세션키는 쿼리에서 받음
  const category = sp.get("category") || "drink";
  const sessionKeyFromQuery = sp.get("session_key") || "";
  const registryStatus = sp.get("registry") || ""; // ?registry=paid 로 돌아오는 경우

  // 혹시나 쿼리가 날아간 경우를 대비해서 localStorage도 한 번 더 참고
  let uploadSession = sessionKeyFromQuery;
  if (!uploadSession) {
    try {
      uploadSession = localStorage.getItem("upload_session") || "";
    } catch {
      /* ignore */
    }
  }
  const sessionKey = uploadSession;

  // 이 세션 전용 로컬스토리지 키
  const transferStorageKey = `julyupay:transfer:${category}:${sessionKey || "default"}`;
  const verifyStorageKey = `julyupay:acctVerify:${user.id}:${category}:${sessionKey || "default"}`;

  // ✅ ContractStart에서 저장해 둔 기타내용 키
  const extraNoteStorageKey = sessionKey ? `julyupay:extraNote:${sessionKey}` : "";

  // ✅ 등기부 주소 + 결제완료 여부를 sessionStorage 에서 복구
  const [registryAddress, setRegistryAddress] = useState<{
    addr1?: string;
    addr2?: string;
    zonecode?: string;
  } | null>(null);
  const [registryPaid, setRegistryPaid] = useState(false);

  // 입력값
  const [holderName, setHolderName] = useState("");
  const [senderName, setSenderName] = useState("");
  const [bankCode, setBankCode] = useState("004");
  const [accountNo, setAccountNo] = useState("");
  const [amount, setAmount] = useState<number | "">("");
  const [amountError, setAmountError] = useState<string>("");
  const [restoredTransferKey, setRestoredTransferKey] = useState<string | null>(null);
  const [hasEntryAmount, setHasEntryAmount] = useState(false);
  const [restoredVerifySnapshot, setRestoredVerifySnapshot] = useState<string | null>(null);

  // ✅ 분할결제
  const [splitEnabled, setSplitEnabled] = useState(false);
  const [splitCount, setSplitCount] = useState<SplitCount>(2);
  const [splitAmounts, setSplitAmounts] = useState<number[]>([0, 0]);

  // 수수료/부가세/총금액
  const [fee, setFee] = useState(0);
  const [vat, setVat] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  // 송금 방식
  const [payMode, setPayMode] = useState<"none" | "now" | "reserve">("none");
  const [reserveDate, setReserveDate] = useState("");

  // 검증 결과
  const [verify, setVerify] = useState<VerifyResp | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 실제 계좌 소유자명(벤더 결과)
  const [actualHolderName, setActualHolderName] = useState<string>("");

  // 등기부 결제 진행 중
  const [registryPaying, setRegistryPaying] = useState(false);

  // ✅ 기타내용 (읽기 전용)
  const [extraNote, setExtraNote] = useState("");

  // ✅ 신청인(로그인 사용자) 이름
  const [applicantName, setApplicantName] = useState("");

  const selectedBank = BANKS.find((b) => b.code === bankCode);
  const selectedBankName = selectedBank?.name || "";

  const numericAmount = typeof amount === "number" ? amount : Number(String(amount) || "0");

  const needsRegistry = category === "rent";
  const registryOk = !needsRegistry || registryPaid;

  const verifiedOk = skipAccountVerify || !!(verify && verify.ok && verify.matched);
  const flowScope = JSON.stringify([user.id, category, sessionKey]);
  const view = readTransferView(location.state, flowScope);
  const transferStep = view.step;
  const amountSheetOpen = view.amountOpen;
  const navigationPending = useRef(false);
  const verifySnapshot = JSON.stringify([verifyStorageKey, holderName.trim(), bankCode, accountNo.replace(/[^\d]/g, "")]);
  const draftReady = restoredTransferKey === transferStorageKey && restoredVerifySnapshot === verifySnapshot;

  // Router history makes the visible Back button and Android WebView Back agree.
  // The URL stays unchanged, so a step change does not refetch authentication or remount the form.
  useEffect(() => { navigationPending.current = false; }, [location.key]);

  function showView(next: TransferView, replace = false) {
    if (navigationPending.current || submitting || registryPaying) return;
    navigationPending.current = true;
    nav(`${location.pathname}${location.search}${location.hash}`, {
      replace,
      state: transferViewState(location.state, flowScope, next),
    });
  }

  function goToEarlierStep(target: TransferStep) {
    if (navigationPending.current || submitting || registryPaying) return;
    const delta = transferBackDelta(view, target);
    if (delta !== null) {
      navigationPending.current = true;
      nav(delta);
    } else {
      showView({ step: target, depth: 0, amountOpen: false }, true);
    }
  }

  function handleStepBack() {
    if (amountSheetOpen) return goToEarlierStep(transferStep);
    if (transferStep > 1) return goToEarlierStep((transferStep - 1) as TransferStep);
    if (navigationPending.current || submitting || registryPaying) return;
    navigationPending.current = true;
    if (hasTransferDocumentHistory(location.state, category, sessionKey)) {
      nav(-1);
    } else {
      nav(transferDocumentPath(category, sessionKey, amountEntry), { replace: true });
    }
  }

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [transferStep]);

  // ✅ splitCount 변경 시 splitAmounts 길이 보정
  useEffect(() => {
    setSplitAmounts((prev) => {
      const next = prev.slice(0, splitCount);
      while (next.length < splitCount) next.push(0);
      return next;
    });
  }, [splitCount]);

  // ✅ 분할 합계/검증
  const splitSum = useMemo(
    () => splitAmounts.slice(0, splitCount).reduce((a, b) => a + (Number(b) || 0), 0),
    [splitAmounts, splitCount]
  );
  const splitOver = splitSum > (Number(numericAmount) || 0);
  const splitExact = splitSum === (Number(numericAmount) || 0);
  const splitAllPositive = splitAmounts.slice(0, splitCount).every((x) => Number(x) > 0);

  // ✅ 회차별/합계 수수료 계산
  const splitBreakdown = useMemo(() => {
    const arr = splitAmounts.slice(0, splitCount).map((x) => Number(x) || 0);
    return arr.map((amt) => ({
      amount: amt,
      ...calcFeeVatTotal(amt),
    }));
  }, [splitAmounts, splitCount]);

  const splitFeeSum = useMemo(() => splitBreakdown.reduce((a, b) => a + (b.fee || 0), 0), [splitBreakdown]);
  const splitVatSum = useMemo(() => splitBreakdown.reduce((a, b) => a + (b.vat || 0), 0), [splitBreakdown]);
  const splitTotalSum = useMemo(() => splitBreakdown.reduce((a, b) => a + (b.total || 0), 0), [splitBreakdown]);

  // ✅ canSubmit용 분할 조건
  const splitOk = !splitEnabled ? true : numericAmount > 0 && !splitOver && splitAllPositive && splitExact;

  // ✅ canSubmit
  const canSubmit =
    numericAmount > 0 &&
    numericAmount <= MAX_AMOUNT &&
    payMode !== "none" &&
    (payMode === "now" || (payMode === "reserve" && !!reserveDate)) &&
    registryOk &&
    verifiedOk &&
    splitOk;

  const transferDateLabel =
    payMode === "now"
      ? new Date().toISOString().slice(0, 10)
      : payMode === "reserve"
        ? reserveDate || "미선택"
        : "미선택";

  // ✅ 원금/분할 변경에 따라 fee/vat/total 업데이트
  useEffect(() => {
    if (!numericAmount || numericAmount <= 0) {
      setFee(0);
      setVat(0);
      setTotalAmount(0);
      return;
    }

    if (!splitEnabled) {
      const r = calcFeeVatTotal(numericAmount);
      setFee(r.fee);
      setVat(r.vat);
      setTotalAmount(r.total);
      return;
    }

    setFee(splitFeeSum);
    setVat(splitVatSum);
    setTotalAmount(splitTotalSum);
  }, [numericAmount, splitEnabled, splitFeeSum, splitVatSum, splitTotalSum]);

  useEffect(() => {
    // Wait for both draft and matching account-result restoration before correcting a refreshed step.
    if (!draftReady) return;
    const allowed = allowedTransferStep(transferStep, verifiedOk, canSubmit);
    if (allowed !== transferStep) goToEarlierStep(allowed);
  }, [draftReady, verifiedOk, canSubmit, transferStep, location.key]);

  // ✅ 등기부 주소 복원
  useEffect(() => {
    if (!sessionKey) return;
    try {
      const addrKey = `julyupay:registryAddress:${sessionKey}`;
      const raw = window.sessionStorage.getItem(addrKey);
      if (raw) {
        const saved = JSON.parse(raw) || {};
        setRegistryAddress({
          addr1: saved.addr1 || "",
          addr2: saved.addr2 || "",
          zonecode: saved.zonecode || "",
        });
      }
    } catch {
      /* ignore */
    }
  }, [sessionKey]);

  // ✅ 등기부 결제 완료 여부 복원
  useEffect(() => {
    if (!sessionKey) return;
    try {
      const paidKey = `julyupay:registryPaid:${sessionKey}`;
      const stored = window.sessionStorage.getItem(paidKey);
      if (stored === "1") setRegistryPaid(true);

      if (registryStatus === "paid") {
        setRegistryPaid(true);
        window.sessionStorage.setItem(paidKey, "1");
      }
    } catch {
      /* ignore */
    }
  }, [sessionKey, registryStatus]);

  // ✅ 기타내용 복원
  useEffect(() => {
    if (!extraNoteStorageKey) return;
    try {
      const raw = localStorage.getItem(extraNoteStorageKey);
      if (raw != null) setExtraNote(raw);
    } catch {
      /* ignore */
    }
  }, [extraNoteStorageKey]);

  // ✅ 신청인명 로드
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await authFetch(`${API_BASE}/api/me`, { method: "GET" });
        const j = await r.json().catch(() => null);
        const nm = String(j?.user?.name || "").trim();
        if (mounted && r.ok && j?.ok && nm) setApplicantName(nm);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // 🔄 이체 폼 값 복원
  useEffect(() => {
    setHasEntryAmount(false);
    try {
      const raw = localStorage.getItem(transferStorageKey);
      if (!raw) return;
      const saved = JSON.parse(raw) || {};

      setHasEntryAmount(amountEntry && parseRegistrationAmount(saved.amount) !== null);
      if (saved.holderName != null) setHolderName(saved.holderName);
      if (saved.senderName != null) setSenderName(saved.senderName);
      if (saved.bankCode) setBankCode(saved.bankCode);
      if (saved.accountNo != null) setAccountNo(saved.accountNo);

      if (saved.splitEnabled != null) setSplitEnabled(!!saved.splitEnabled);
      if (saved.splitCount != null) {
        const sc = Number(saved.splitCount);
        if (sc === 1 || sc === 2 || sc === 3) setSplitCount(sc as SplitCount);
      }
      if (Array.isArray(saved.splitAmounts)) {
        const arr = saved.splitAmounts.map((x: any) => Number(x) || 0);
        if (arr.length) setSplitAmounts(arr);
      }

      if (saved.amount !== undefined && saved.amount !== "") {
        let n = Number(saved.amount);
        if (!Number.isNaN(n) && n > 0) {
          if (n > MAX_AMOUNT) {
            n = MAX_AMOUNT;
            setAmountError("이체금액은 2,000만원 이하까지만 입력하실 수 있습니다.");
          }
          setAmount(n);
        }
      }

      if (saved.payMode) setPayMode(saved.payMode);
      if (saved.reserveDate) setReserveDate(saved.reserveDate);
    } catch (e) {
      console.warn("restore transfer form failed", e);
    } finally {
      setRestoredTransferKey(transferStorageKey);
    }
  }, [transferStorageKey, amountEntry]);

  // 🔄 계좌인증 결과 복원 (입력 값 동일할 때만)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(verifyStorageKey);
      if (!raw) return;

      const saved = JSON.parse(raw) || {};
      const currentAcc = accountNo.replace(/[^\d]/g, "");
      const nameTrim = holderName.trim();

      if (
        saved.verified &&
        saved.holderName === nameTrim &&
        saved.bankCode === bankCode &&
        saved.accountNo === currentAcc &&
        nameTrim &&
        currentAcc
      ) {
        setVerify({
          ok: true,
          matched: true,
          reason: saved.reason || "",
          holderNameInput: saved.holderNameInput || saved.holderName,
          vendorHolderName: saved.vendorHolderName === undefined ? null : saved.vendorHolderName,
        });
        setActualHolderName(String(saved.vendorHolderName || ""));
      } else {
        setVerify(null);
        setActualHolderName("");
      }
    } catch (e) {
      console.warn("restore verify failed", e);
    } finally {
      setRestoredVerifySnapshot(verifySnapshot);
    }
  }, [verifyStorageKey, holderName, bankCode, accountNo, verifySnapshot]);

  // 🔄 입력 변경 시 자동 저장
  useEffect(() => {
    // Restore first so a remount cannot overwrite the saved draft with initial empty values.
    if (restoredTransferKey !== transferStorageKey) return;
    const state = {
      holderName,
      senderName,
      bankCode,
      accountNo,
      amount: amount === "" ? "" : Number(amount),
      payMode,
      reserveDate,
      splitEnabled,
      splitCount,
      splitAmounts,
    };
    try {
      localStorage.setItem(transferStorageKey, JSON.stringify(state));
    } catch (e) {
      console.warn("save transfer form failed", e);
    }
  }, [
    holderName,
    senderName,
    bankCode,
    accountNo,
    amount,
    payMode,
    reserveDate,
    splitEnabled,
    splitCount,
    splitAmounts,
    transferStorageKey,
    restoredTransferKey,
  ]);
  // ====================== BLOCK 2/3 (계좌인증/등기부결제/승인요청 로직) ======================

  /** ✅ 계좌인증 (중요: authFetch 사용해서 토큰 붙임 → vendor_failed 감소) */
  async function doVerifyAccount(nameOverride?: string) {
    setVerify(null);
    setActualHolderName("");

    const acc = accountNo.replace(/[^\d]/g, "");
    const name = (nameOverride ?? holderName).trim();

    if (!name) return alert("상대방 정보를 입력하세요.");
    if (!acc) return alert("상대방 계좌 정보를 입력하세요.");

    setVerifying(true);
    try {
      // ✅ 여기서 fetch 금지. authFetch 사용.
      const r = await authFetch(`${API_BASE}/api/apick/account/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bank_code: bankCode,
          account: acc,
          holderName: name,
        }),
      });

      const j: VerifyResp = await r.json().catch(() => ({ ok: false, error: "bad_json" } as any));

      // 벤더 계좌 소유자명 후보
      const vendorName =
        (j?.vendorHolderName ?? "") ||
        (j?.vendorInfo?.holder_name ?? "") ||
        (j?.vendorInfo?.holderName ?? "") ||
        (j?.vendorInfo?.depositor ?? "") ||
        (j?.vendorInfo?.depositor_name ?? "") ||
        "";

      const vn = String(vendorName || "").trim();
      if (vn) setActualHolderName(vn);

      // ✅ 서버 matched=false라도 정규화 후 동일하면 true로 보정(공백/중간점 오탐 제거)
      const inputNorm = normalizeHolderName(name);
      const vendorNorm = normalizeHolderName(vn);
      const normalizedMatch = !!(inputNorm && vendorNorm && inputNorm === vendorNorm);

      const patched: VerifyResp = {
        ...j,
        ok: !!j?.ok,
        matched: !!(j?.matched || normalizedMatch),
        vendorHolderName: j?.vendorHolderName === undefined ? (vn ? vn : null) : j.vendorHolderName,
      };

      // ✅ vendor 호출이 실패한 경우(reason/vendor_failed 등) -> UI에서 "조회 실패"로 보이게
      if (!patched.ok && !patched.reason && !patched.error) {
        patched.reason = "vendor_failed";
      }

      setVerify(patched);

      if (patched.ok && patched.matched) {
        const toSave = {
          verified: true,
          holderName: name,
          bankCode,
          accountNo: acc,
          holderNameInput: patched.holderNameInput || name,
          vendorHolderName: patched.vendorHolderName === undefined ? null : patched.vendorHolderName,
          reason: patched.reason || "",
        };
        try {
          localStorage.setItem(verifyStorageKey, JSON.stringify(toSave));
        } catch {
          /* ignore */
        }
      } else {
        try {
          localStorage.removeItem(verifyStorageKey);
        } catch {
          /* ignore */
        }
      }
    } catch (e: any) {
      setVerify({ ok: false, error: String(e?.message || e), reason: "vendor_failed" });
      try {
        localStorage.removeItem(verifyStorageKey);
      } catch {
        /* ignore */
      }
    } finally {
      setVerifying(false);
    }
  }

  async function onVerifyAccount() {
    if (skipAccountVerify) return;
    return doVerifyAccount();
  }

  // ✅ 등기부등본 결제 시작
  async function onRegistryPay() {
    if (!sessionKey) {
      alert("등기부등본 결제에 필요한 세션 정보가 없습니다. 다시 시도해 주세요.");
      return;
    }
    if (!registryAddress || !registryAddress.addr1 || !registryAddress.addr2 || !registryAddress.zonecode) {
      alert("등기부등본 발급용 주소를 먼저 모두 입력해 주세요.");
      return;
    }

    // 결제창으로 이동하기 전에 현재 이체 정보 저장
    try {
      const snap = {
        holderName,
        senderName,
        bankCode,
        accountNo,
        amount: amount === "" ? "" : Number(amount),
        payMode,
        reserveDate,
        splitEnabled,
        splitCount,
        splitAmounts,
      };
      localStorage.setItem(transferStorageKey, JSON.stringify(snap));
    } catch {
      /* ignore */
    }

    setRegistryPaying(true);
    try {
      const resp = await authFetch(`${API_BASE}/api/payments/registry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_key: sessionKey,
          amount: 1200,
          category,
          registry_address: registryAddress,
        }),
      });

      const text = await resp.text();
      let data: any = null;
      try {
        data = JSON.parse(text);
      } catch { }

      const payUrl: string | undefined = data?.pay_url || data?.payment?.pay_url;

      if (!resp.ok || !data || !payUrl) {
        const msg = data?.message || `등기부등본 결제 요청 중 오류가 발생했습니다. (status=${resp.status})`;
        alert(msg);
        return;
      }

      window.location.href = payUrl;
    } catch (e: any) {
      console.error("[REGISTRY] request error", e);
      alert("등기부등본 결제 요청 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.");
    } finally {
      setRegistryPaying(false);
    }
  }

  // ✅ 분할 도우미
  function onSplitEven() {
    const T = Math.max(0, Number(numericAmount) || 0);
    if (!T) return;
    const n = splitCount;
    const base = Math.floor(T / n);
    const rem = T - base * n;
    const next = Array.from({ length: n }, (_, i) => base + (i === n - 1 ? rem : 0));
    setSplitAmounts(next);
  }

  function onSplitFillRemainingToLast() {
    const T = Math.max(0, Number(numericAmount) || 0);
    if (!T) return;

    setSplitAmounts((prev) => {
      const n = splitCount;
      const next = prev.slice(0, n);
      while (next.length < n) next.push(0);

      const sumExceptLast = next.slice(0, n - 1).reduce((a, b) => a + (Number(b) || 0), 0);
      next[n - 1] = Math.max(0, T - sumExceptLast);
      return next;
    });
  }

  // 승인요청 → 결제요청 레코드 생성(상태 requested)
  async function onRequestApprove() {
    const acc = accountNo.replace(/[^\d]/g, "");
    const nm = holderName.trim();
    const amt = numericAmount;
    const sender = senderName.trim();

    if (!nm) return alert("상대방 정보를 입력하세요.");
    if (!acc) return alert("상대방 계좌 정보를 입력하세요.");
    if (!amt || amt <= 0) return alert("이체금액(원)을 입력하세요.");
    if (amt > MAX_AMOUNT) return alert("이체금액은 2,000만원 이하까지만 신청하실 수 있습니다.");

    if (splitEnabled) {
      const arr = splitAmounts.slice(0, splitCount).map((x) => Number(x) || 0);
      const sum = arr.reduce((a, b) => a + b, 0);
      if (arr.some((x) => x <= 0)) return alert("분할 금액은 모두 0원보다 커야 합니다.");
      if (sum > amt) return alert("분할 합계가 원금을 초과할 수 없습니다.");
      if (sum !== amt) return alert("분할 합계가 원금과 정확히 일치해야 합니다.");
    }

    if (payMode === "reserve" && !reserveDate) return alert("예약 송금일을 선택하세요.");
    if (needsRegistry && !registryPaid) return alert("등기부등본 결제를 먼저 완료해 주세요.");
    if (!verifiedOk) return alert("계좌인증을 먼저 완료해 주세요.");

    let transfer_date: string | null = null;
    if (payMode === "now") transfer_date = new Date().toISOString().slice(0, 10);
    if (payMode === "reserve") transfer_date = reserveDate || null;

    const applicant = applicantName.trim();

    setSubmitting(true);
    try {
      const payload: any = {
        title: splitEnabled ? `이체 결제 (${nm}) - 분할 ${splitCount}회` : `이체 결제 (${nm})`,
        amount: amt,
        method: category,
        category,
        vat_double_refund_notice_agreed: true,
        vat_notice_version: VAT_NOTICE_VERSION,

        fee_amount: fee,
        vat_amount: vat,
        total_amount: totalAmount,
        fee_policy: splitEnabled ? "per_txn_sum" : "single",

        receiver_name: nm,
        bank_name: selectedBankName,
        bank_code: bankCode,
        account_no: acc,
        transfer_date,

        // ✅ customer_name은 거래상대 예금주
        customer_name: nm,
      };

      if (splitEnabled) {
        const arr = splitAmounts.slice(0, splitCount).map((x) => Number(x) || 0);
        payload.split_enabled = true;
        payload.split_count = splitCount;
        payload.split_amounts = arr;
        payload.splits = arr;
        payload.split_breakdown = arr.map((a, idx) => {
          const r = calcFeeVatTotal(a);
          return {
            split_index: idx + 1,
            amount: a,
            fee_amount: r.fee,
            vat_amount: r.vat,
            total_amount: r.total,
          };
        });
      }

      if (applicant) payload.applicant_name = applicant;

      const senderToSend = sender || applicant;
      if (senderToSend) payload.sender_name = senderToSend;

      if (extraNoteStorageKey && extraNote.trim()) payload.extra_note = extraNote.trim();

      if (sessionKey) {
        payload.upload_session = sessionKey;
        payload.session_key = sessionKey;
      }

      if (registryAddress) payload.registry_address = registryAddress;

      const endpoints = splitEnabled
        ? [`${API_BASE}/api/payments/request/split`, `${API_BASE}/api/payments/request`, `${API_BASE}/api/payments`, `${API_BASE}/api/pay/request`]
        : [`${API_BASE}/api/payments/request`, `${API_BASE}/api/payments`, `${API_BASE}/api/pay/request`];

      let ok = false;
      let lastErr = "";

      for (const url of endpoints) {
        try {
          const r = await authFetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          const j = await r.json().catch(() => null);
          if (r.ok && j?.ok) {
            ok = true;
            break;
          }
          lastErr = j?.message || `HTTP ${r.status}`;
        } catch (e: any) {
          lastErr = String(e?.message || e);
        }
      }

      if (!ok) throw new Error(lastErr || "결제요청 생성 실패");


      try {
        localStorage.removeItem(transferStorageKey);
        localStorage.removeItem(verifyStorageKey);
        if (extraNoteStorageKey) localStorage.removeItem(extraNoteStorageKey);
      } catch { }

      nav("/pay", { replace: true, state: { requestSubmitted: true } });
    } catch (e: any) {
      alert(e?.message || "승인요청 처리 실패");
    } finally {
      setSubmitting(false);
    }
  }

  const inputBase = "jp-payment-input w-full";

  const cardBase = "jp-transfer-panel";
  const fieldLabel = "jp-transfer-label";
  const sectionLabel = "jp-transfer-section-label";
  const panelBase = "jp-transfer-subpanel";
  const stepTitle = transferStepTitle(transferStep, hasEntryAmount);

  if (restoredTransferKey !== transferStorageKey || restoredVerifySnapshot === null || allowedTransferStep(transferStep, verifiedOk, canSubmit) !== transferStep) {
    return <div className="mx-auto max-w-md px-5 py-12 text-center text-sm text-slate-500" role="status">작성한 내용을 불러오고 있어요.</div>;
  }

  return (
    <div className="jp-payment-page">
      <PaymentStepFrame
        phase={2}
        step={transferStep}
        steps={["수취 계좌", hasEntryAmount ? "일정·분할결제" : "금액·일정", "최종 확인"]}
        progress={hasEntryAmount ? { step: transferStep === 3 ? 3 : 2, steps: ["송금 금액", "정보·서류", "최종 확인"] } : undefined}
        title={stepTitle}
        description={transferStep === 3 ? "관리자 승인 후 카드결제가 가능합니다." : undefined}
        onBack={handleStepBack}
        busy={submitting || registryPaying}
      >
        {transferStep === 1 ? (
          <section className={cardBase + " space-y-3 sm:space-y-5"}>
            <div>
              <label className={fieldLabel} htmlFor="jp-holder-name">받는 분 예금주명</label>
              <input
                id="jp-holder-name"
                className={inputBase}
                value={holderName}
                placeholder="예금주명"
                onChange={(e) => {
                  setHolderName(e.target.value);
                  setVerify(null);
                  setActualHolderName("");
                  try {
                    localStorage.removeItem(verifyStorageKey);
                  } catch {}
                }}
              />
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className={fieldLabel} htmlFor="jp-bank-code">은행</label>
                <select
                  id="jp-bank-code"
                  className={inputBase}
                  value={bankCode}
                  onChange={(e) => {
                    setBankCode(e.target.value);
                    setVerify(null);
                    setActualHolderName("");
                    try {
                      localStorage.removeItem(verifyStorageKey);
                    } catch {}
                  }}
                >
                  {BANKS.map((b) => (
                    <option key={b.code} value={b.code}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={fieldLabel} htmlFor="jp-account-number">받는 분 계좌번호</label>
                <input
                  id="jp-account-number"
                  className={inputBase}
                  value={accountNo}
                  placeholder="계좌번호"
                  onChange={(e) => {
                    setAccountNo(e.target.value);
                    setVerify(null);
                    setActualHolderName("");
                    try {
                      localStorage.removeItem(verifyStorageKey);
                    } catch {}
                  }}
                  inputMode="numeric"
                />
              </div>

              {skipAccountVerify ? null : (
                <button
                  type="button"
                  onClick={onVerifyAccount}
                  disabled={verifying || verifiedOk}
                  className={verifiedOk ? "jp-flow-secondary" : "jp-flow-primary"}
                >
                  {verifying ? "확인 중…" : verifiedOk ? "계좌 확인 완료" : "계좌 확인하기"}
                </button>
              )}
            </div>

            <div>
              <label className={fieldLabel} htmlFor="jp-sender-name">송금자명 <span className="font-normal text-slate-400">(선택)</span></label>
              <input
                id="jp-sender-name"
                className={inputBase}
                placeholder="미입력 시 신청인 이름"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
              />
              
            </div>

            {!skipAccountVerify && verify ? (
              verify.ok && verify.matched ? (
                <div className="jp-account-status is-success">
                  예금주가 일치해요{actualHolderName ? ` · ${actualHolderName}` : ""}
                </div>
              ) : (
                <div className="jp-account-status is-error">
                  예금주 또는 계좌를 확인해 주세요{actualHolderName ? ` · 조회명: ${actualHolderName}` : ""}
                </div>
              )
            ) : null}

            {extraNote.trim() ? (
              <div className={panelBase}>
                <div className={fieldLabel}>요청사항</div>
                <div className="whitespace-pre-wrap text-sm leading-6 text-slate-700">{extraNote}</div>
              </div>
            ) : null}
          </section>
        ) : null}

        {transferStep === 2 ? (
          <section className={cardBase + " space-y-3 sm:space-y-5"}>
            {hasEntryAmount ? (
              <div className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-4 py-3">
                <div>
                  <div className="mb-1 text-xs text-slate-500">송금 금액</div>
                  <strong className="text-xl font-semibold text-slate-900">{numericAmount > 0 ? `${numericAmount.toLocaleString("ko-KR")}원` : "금액 입력 필요"}</strong>
                </div>
                <button type="button" className="min-h-11 shrink-0 px-2 text-sm font-semibold text-orange-700 underline underline-offset-4" onClick={() => showView({ ...view, amountOpen: true })} aria-haspopup="dialog">금액 변경</button>
              </div>
            ) : <div className={panelBase}>
              <div className={fieldLabel}>거래처에 보낼 금액</div>
              <button
                type="button"
                onClick={() => showView({ ...view, amountOpen: true })}
                className="jp-transfer-amount"
                aria-label={numericAmount > 0 ? `송금액 ${numericAmount.toLocaleString("ko-KR")}원 수정` : "송금액 입력"}
                aria-haspopup="dialog"
              >
                <span className={!numericAmount ? "is-placeholder" : undefined}>{numericAmount > 0 ? numericAmount.toLocaleString("ko-KR") : "금액 입력"}</span>
                <span className="jp-transfer-amount-unit">원</span>
              </button>
            </div>}
            {amountError ? <p className="mt-2 text-xs font-semibold text-rose-600">{amountError}</p> : null}

            <div className={panelBase}>
              <div className="jp-split-toggle-row">
                <div>분할결제</div>
                <button
                  type="button"
                  onClick={() => setSplitEnabled((v) => !v)}
                  disabled={!numericAmount}
                  aria-label="분할결제 사용"
                  aria-pressed={splitEnabled}
                  className="jp-split-toggle"
                >
                  <span>{splitEnabled ? "사용 중" : "사용 안 함"}</span>
                  <span className="jp-split-toggle-track" aria-hidden="true"><span /></span>
                </button>
              </div>

              {splitEnabled ? (
                <div className="jp-split-panel">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm leading-6 text-slate-600">분할 합계: {numericAmount.toLocaleString()}원</div>
                    <select
                      aria-label="분할 횟수"
                      className="rounded-[16px] border border-orange-100 bg-white px-3 py-2 text-sm font-bold text-slate-700 focus:border-[#f97316] focus:outline-none focus:ring-4 focus:ring-[#f97316]/10"
                      value={splitCount}
                      onChange={(e) => setSplitCount(Number(e.target.value) as SplitCount)}
                    >
                      <option value={1}>1회</option>
                      <option value={2}>2회</option>
                      <option value={3}>3회</option>
                    </select>
                  </div>

                  <div className="jp-split-tools">
                    <button type="button" onClick={onSplitEven} className="rounded-[16px] border border-orange-100 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-orange-50">균등분할</button>
                    <button type="button" onClick={onSplitFillRemainingToLast} className="rounded-[16px] border border-orange-100 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-orange-50">잔액 채우기</button>
                  </div>

                  <div className="mt-4 space-y-2">
                    {splitAmounts.slice(0, splitCount).map((v, idx) => (
                      <div key={idx} className="jp-split-amount-row">
                        <div className="text-sm font-semibold text-slate-600">분할 {idx + 1}</div>
                        <input
                          inputMode="numeric"
                          aria-label={`분할 ${idx + 1} 원금`}
                          className={inputBase}
                          value={v ? String(v.toLocaleString("ko-KR")) : ""}
                          onChange={(e) => {
                            const n = onlyDigitsToNumber(e.target.value);
                            setSplitAmounts((prev) => prev.map((x, i) => (i === idx ? n : x)));
                          }}
                          placeholder="금액(원)"
                        />
                      </div>
                    ))}
                  </div>

                  <div className="jp-split-breakdown">
                    {splitBreakdown.map((r, i) => (
                      <div key={i} className="mb-2 last:mb-0">
                        <div>{i + 1}차 원금 {r.amount.toLocaleString()}원</div>
                        <div className="jp-split-breakdown-detail">수수료 {r.fee.toLocaleString()}원 · 부가세 {r.vat.toLocaleString()}원 · 총 {r.total.toLocaleString()}원</div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-3 text-sm">
                    <div className="flex justify-between"><span className="text-slate-600">분할 합계</span><span className={splitOver ? "font-semibold text-rose-600" : "font-semibold text-slate-900"}>{splitSum.toLocaleString()}원</span></div>
                    <div className="mt-1 flex justify-between"><span className="text-slate-600">남은 금액</span><span className={splitOver ? "font-semibold text-rose-600" : "text-slate-900"}>{(numericAmount - splitSum).toLocaleString()}원</span></div>
                    {splitOver ? <div className="mt-2 text-rose-600">분할 합계가 원금을 초과했습니다.</div> : null}
                    {!splitOver && !splitExact ? <div className="mt-2 text-amber-600">분할 합계가 원금과 일치하지 않습니다.</div> : null}
                    {!splitOver && splitExact && splitAllPositive ? <div className="mt-2 font-semibold text-emerald-700">분할 금액 설정 완료</div> : null}
                  </div>
                </div>
              ) : null}
            </div>

            {numericAmount > 0 ? (
              <div className="jp-transfer-totals">
                <div><span>이용료 3.7%</span><strong>{fee.toLocaleString()}원</strong></div>
                <div><span>이용료 부가세</span><strong>{vat.toLocaleString()}원</strong></div>
                <div className="jp-transfer-total"><span>총 결제금액</span><strong>{totalAmount.toLocaleString()}원</strong></div>
                {splitEnabled ? <p>수수료·부가세는 분할 건별로 계산됩니다.</p> : null}
              </div>
            ) : null}

            <div className={panelBase}>
              <div className={sectionLabel}>송금 일정</div>
              <div className="jp-transfer-schedule">
                <button
                  type="button"
                  aria-pressed={payMode === "now"}
                  disabled={!numericAmount}
                  onClick={() => {
                    setPayMode("now");
                    const iso = new Date().toISOString().slice(0, 10);
                    setReserveDate(iso);
                  }}
                  className={[
                    "rounded-[18px] border px-4 py-2.5 text-sm font-extrabold transition",
                    payMode === "now" ? "border-[#f97316] bg-white text-[#f97316]" : "border-[#fed7aa] bg-white text-slate-700 hover:bg-slate-50",
                    "disabled:opacity-50",
                  ].join(" ")}
                >
                  바로송금
                </button>
                <button
                  type="button"
                  aria-pressed={payMode === "reserve"}
                  disabled={!numericAmount}
                  onClick={() => {
                    setPayMode("reserve");
                    setReserveDate("");
                  }}
                  className={[
                    "rounded-[18px] border px-4 py-2.5 text-sm font-extrabold transition",
                    payMode === "reserve" ? "border-[#f97316] bg-white text-[#f97316]" : "border-[#fed7aa] bg-white text-slate-700 hover:bg-slate-50",
                    "disabled:opacity-50",
                  ].join(" ")}
                >
                  예약송금
                </button>
                {payMode === "reserve" ? (
                  <input
                    type="date"
                    aria-label="예약 송금일"
                    value={reserveDate}
                    onChange={(e) => setReserveDate(e.target.value)}
                    min={new Date().toISOString().slice(0, 10)}
                    className="rounded-[18px] border border-orange-100 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 focus:border-[#f97316] focus:outline-none focus:ring-4 focus:ring-[#f97316]/10"
                  />
                ) : null}
              </div>
            </div>
          </section>
        ) : null}

        {transferStep === 3 ? (
          <section className="jp-transfer-review">
            <div className="jp-transfer-review-grid">
              <div className="font-semibold text-slate-500">받는 분</div>
              <div className="text-right font-extrabold text-slate-950">{holderName || "미입력"}</div>
              <div className="font-semibold text-slate-500">계좌</div>
              <div className="text-right font-semibold text-slate-900">{selectedBankName || "은행 미선택"} / {accountNo || "미입력"}</div>
              <div className="font-semibold text-slate-500">송금자명</div>
              <div className="text-right font-semibold text-slate-900">{senderName || applicantName || "미입력"}</div>
              <div className="font-semibold text-slate-500">원금</div>
              <div className="text-right font-extrabold text-slate-950">{numericAmount.toLocaleString()}원</div>
              <div className="font-semibold text-slate-500">송금 일정</div>
              <div className="text-right font-semibold text-slate-900">
                {payMode === "now" ? "바로송금" : payMode === "reserve" ? "예약송금" : "미선택"}
                {transferDateLabel !== "미선택" ? ` (${transferDateLabel})` : ""}
              </div>
            </div>

            {splitEnabled ? (
              <div className="mt-4 rounded-[18px] bg-white p-3">
                <div className="text-xs font-extrabold text-slate-500">분할결제</div>
                <div className="mt-2 text-sm text-slate-700">총 {splitCount}회</div>
                <div className="mt-2 space-y-1 text-sm text-slate-600">
                  {splitBreakdown.map((r, i) => (
                    <div key={i}>{i + 1}차 {r.amount.toLocaleString()}원 / 총 {r.total.toLocaleString()}원</div>
                  ))}
                </div>
              </div>
            ) : null}

            {extraNote.trim() ? (
              <div className="mt-4 rounded-[18px] bg-white p-3">
                <div className="text-xs font-extrabold text-slate-500">요청사항</div>
                <div className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700">{extraNote}</div>
              </div>
            ) : null}
            <div className="jp-transfer-review-total">
              <span>총 카드결제 금액</span>
              <strong>{totalAmount.toLocaleString()}<small>원</small></strong>
              <p>이용료 {fee.toLocaleString()}원 · 부가세 {vat.toLocaleString()}원 포함</p>
            </div>
          </section>
        ) : null}

      <section className="jp-transfer-actions">
        <div className="mx-auto flex w-full max-w-xl justify-center">
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
            {transferStep === 3 && category === "rent" ? (
              <button
                type="button"
                onClick={onRegistryPay}
                disabled={registryPaying || registryPaid}
                className={[
                  "rounded-[18px] px-5 py-3 text-sm font-extrabold text-white shadow-[0_14px_28px_rgba(249,115,22,0.22)] transition",
                  registryPaid ? "cursor-not-allowed bg-[#fed7aa]" : "bg-[#f97316] hover:bg-[#ea580c] active:translate-y-px",
                  "disabled:cursor-not-allowed",
                ].join(" ")}
              >
                {registryPaid ? "등기부 결제 완료" : registryPaying ? "결제 준비 중..." : "등기부등본 결제"}
              </button>
            ) : null}

            {transferStep === 1 && verifiedOk ? (
              <button
                type="button"
                onClick={() => showView(nextTransferView(view))}
                disabled={!verifiedOk}
                className="jp-flow-primary"
              >
                다음
              </button>
            ) : null}

            {transferStep === 2 ? (
              <button
                type="button"
                onClick={() => showView(nextTransferView(view))}
                disabled={!canSubmit}
                className="jp-flow-primary"
              >
                최종 확인
              </button>
            ) : null}

            {transferStep === 3 ? (
              <button
                type="button"
                onClick={onRequestApprove}
                disabled={submitting || !canSubmit}
                className="jp-flow-primary"
              >
                {submitting ? "요청 중..." : "승인 요청하기"}
              </button>
            ) : null}
          </div>
        </div>
      </section>
      </PaymentStepFrame>

      {amountSheetOpen ? (
        <div className="jp-payment-keypad jp-payment-dialog fixed inset-0 z-50 flex items-end bg-slate-950/35 px-3 pb-[calc(env(safe-area-inset-bottom)+84px)] pt-10 sm:items-center sm:justify-center sm:p-6">
          <div role="dialog" aria-modal="true" aria-labelledby="jp-amount-title" className="max-h-[calc(100dvh-120px)] w-full max-w-md overflow-y-auto rounded-[28px] bg-white p-4 shadow-[0_24px_70px_rgba(15,23,42,0.28)] sm:rounded-[32px]">
            <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-[#d4dceb]" />
            <div className="mb-3 flex items-start justify-between gap-4">
              <div>
                <h2 id="jp-amount-title" className="text-2xl font-bold text-slate-950">금액 입력</h2>
              </div>
              <button type="button" className="rounded-full bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-600" onClick={() => goToEarlierStep(transferStep)}>
                닫기
              </button>
            </div>

            <div className="jp-keypad-value mb-3 rounded-[22px] border border-slate-200 bg-white px-4 py-4 text-center text-[30px] font-black text-slate-950 sm:rounded-[24px] sm:py-5 sm:text-[34px]">
              {numericAmount > 0 ? `${numericAmount.toLocaleString("ko-KR")}원` : "0원"}
            </div>

            <div className="jp-keypad-shortcuts mb-3 grid grid-cols-4 gap-2">
              {[10000, 50000, 100000, 1000000].map((unit) => (
                <button
                  key={unit}
                  type="button"
                  onClick={() => {
                    const next = numericAmount + unit;
                    if (next > MAX_AMOUNT) {
                      setAmount(MAX_AMOUNT);
                      setAmountError("이체금액은 2,000만원 이하까지만 입력하실 수 있습니다.");
                    } else {
                      setAmount(next);
                      setAmountError("");
                    }
                  }}
                  className="rounded-full border border-orange-100 bg-white px-2 py-2 text-xs font-bold text-slate-700 sm:px-3 sm:text-sm"
                >
                  + {unit >= 10000 ? `${unit / 10000}만` : unit.toLocaleString("ko-KR")}
                </button>
              ))}
            </div>

            <div className="jp-keypad-digits grid grid-cols-3 gap-2 sm:gap-3">
              {[1,2,3,4,5,6,7,8,9,"00",0,"del"].map((key) => (
                <button
                  key={String(key)}
                  type="button"
                  onClick={() => {
                    if (key == "del") {
                      const raw = String(numericAmount || "");
                      const nextRaw = raw.slice(0, -1);
                      if (!nextRaw) {
                        setAmount("");
                        setPayMode("none");
                        setAmountError("");
                      } else {
                        setAmount(Number(nextRaw));
                        setAmountError("");
                      }
                      return;
                    }
                    const raw = `${numericAmount || ""}${key}`;
                    const next = Number(raw);
                    if (!Number.isFinite(next)) return;
                    if (next > MAX_AMOUNT) {
                      setAmount(MAX_AMOUNT);
                      setAmountError("이체금액은 2,000만원 이하까지만 입력하실 수 있습니다.");
                      return;
                    }
                    setAmount(next);
                    setAmountError("");
                  }}
                  className="flex h-12 items-center justify-center rounded-[16px] bg-slate-100 text-2xl font-semibold text-slate-900 transition hover:bg-slate-200 sm:h-16 sm:rounded-[20px] sm:text-3xl"
                >
                  {key === "del" ? "←" : String(key)}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => goToEarlierStep(transferStep)}
              className="mt-3 inline-flex w-full items-center justify-center rounded-[16px] bg-[#f97316] px-6 py-2.5 text-sm font-extrabold text-white shadow-[0_14px_28px_rgba(249,115,22,0.24)] transition hover:bg-[#ea580c] sm:rounded-[18px] sm:py-3"
            >
              적용
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
